<script setup>
import { useBreakpoints } from "@vueuse/core";
import { format } from "date-fns";
import mentor1 from "~/assets/img/mentor-profiles/mentor1.jpg";
import mentor2 from "~/assets/img/mentor-profiles/mentor2.jpg";
import mentor3 from "~/assets/img/mentor-profiles/mentor3.jpg";
import mentor4 from "~/assets/img/mentor-profiles/mentor4.jpg";
import mentor5 from "~/assets/img/mentor-profiles/mentor5.jpg";

definePageMeta({
  auth: false,
});

const sidebarItems = ref([
  "Key information",
  "About university",
  "Programs",
  "Scholarship",
  "Review",
  // "Campus features",
  "Location",
  // "Accommodation",
  // "Notable alumni",
  // "News",
]);

const activeSidebar = ref("Key information");
const breakpoints = useBreakpoints(BREAKPOINTS);
const isMobile = computed(() => breakpoints.smaller("desktop"));

const activeBreakpoint = breakpoints.active();
const slidesPerView = computed(() => {
  switch (activeBreakpoint.value) {
    case "mobile":
      return 1;
    case "tablet":
      return 1.5;
    case "laptop":
      return 2.5;
    case "desktop":
      return 2.5;
    default:
      return 1;
  }
});

const route = useRoute();

const items = ref([
  { icon: "pi-building", title: "Founded year", value: "" },
  { icon: "pi-map-marker", title: "Programs", value: "" },
  { icon: "pi-user", title: "Number of Student Mentors", value: "" },
  { icon: "pi-flag", title: "Location", value: "" },
]);

const { data: degrees } = useFetch("/api/degree");

const { data: university } = await usePublicFetch(
  () => `/universities/${route.params.universitySlug}`,
  {
    key: "university-detail-by-slug",
  },
);
console.log("university", university.value);

const { data: exploreCount } = usePublicFetch("explore-count", {
  method: "GET",
  key: "explore-count",
});

function selectSidebar(name) {
  activeSidebar.value = name;

  const target = document.getElementById(name);
  if (target) {
    target.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }
}

const universityImages = computed(() => {
  return university?.value?.data?.media
    ?.filter((media) => media.collection_name === "university_images")
    ?.map((media) => ({
      itemImageSrc: media?.original_url,
      thumbnailImageSrc: media?.original_url,
      alt: university?.value?.data?.name,
    }));
});

const getTestimonialImage = (media) => {
  if (!media || !Array.isArray(media)) return null;

  const testimonialImage = media.find(
    (item) => item?.collection_name === "student_testimonial_images",
  );
  return testimonialImage?.original_url || null;
};

const programsGroupByDegree = computed(() => {
  if (!university?.value?.data?.programs?.data) return null;
  const programs = university?.value?.data?.programs?.data;
  const result = {};
  programs.forEach((program) => {
    const degree = program?.attributes?.data?.find(
      (data) => data.key === "degree",
    )?.value;
    if (!degree) return;
    if (!result[degree]) {
      result[degree] = [];
    }
    result[degree].push(program);
  });
  return result;
});

function getAttributeData(key) {
  const attribute = university?.value?.data?.attributes?.data?.find(
    (d) => d.key === key,
  );
  return attribute ? attribute.value : "";
}

const mentorImages = [mentor1, mentor2, mentor3, mentor4, mentor5];

useHead({
  link: [
    {
      rel: "canonical",
      href: `https://unilinks.co/universities/${route.params.universitySlug}`,
    },
  ],
});

useSeoMeta({
  title: `Unilinks - ${university.value?.data.name}`,
  ogTitle: `Unilinks - ${university.value?.data.name}`,
  description:
    "Facing university hurdles? Our program consultation is here to help you conquer them",
  ogDescription:
    "Facing university hurdles? Our program consultation is here to help you conquer them",
  ogImage: META_IMAGE_URL,
  ogUrl: `https://unilinks.co/universities/${route.params.universitySlug}`,
  twitterCard: "summary_large_image",
  twitterTitle: `Unilinks - ${university.value?.data.name}`,
  twitterDescription:
    "Facing university hurdles? Our program consultation is here to help you conquer them",
  twitterImage: META_IMAGE_URL,
});
</script>

<template>
  <NuxtLayout name="primary">
    <div class="w-full">
      <!-- desktop -->
      <div
        class="hidden md:block mx-auto w-full max-w-7xl px-4 lg:px-0 mb-12 mt-6"
      >
        <div class="mx-4">
          <div class="flex items-start justify-between my-3">
            <div class="flex items-start gap-6">
              <Avatar
                v-if="getUniversityLogo(university?.data?.media)"
                :image="getUniversityLogo(university?.data?.media)"
                class="mr-2 w-20 h-20"
                size="xlarge"
              />
              <Avatar
                v-else
                :label="university?.data?.name?.slice(0, 1)"
                class="mr-2 w-20 h-20"
                size="xlarge"
              />

              <div class="space-y-2">
                <h1 class="text-primary-500 font-bold text-3xl">
                  {{ university?.data?.name }}
                </h1>

                <div class="flex items-center">
                  <div class="flex items-center gap-3">
                    <p class="">{{ university?.data?.type }}</p>
                  </div>

                  <Divider layout="vertical" class="!py-2" />

                  <div class="flex items-center gap-3">
                    <p class="">
                      {{ getRankingLabel(university?.data?.ranking) }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- <Button
              icon="pi pi-heart"
              label="Wishlist"
              class="text-[#1C477B] !text-sm"
              text
            /> -->
            <WishlistBtn :id="university?.data?.id" />
          </div>

          <div class="relative">
            <!-- Banner -->
            <div
              v-if="!universityImages"
              class="my-8 w-full h-[560px] rounded-lg bg-slate-200"
            ></div>

            <CustomCarousel v-else :slidesPerView="1" :data="universityImages">
              <template #default="{ data: media }">
                <div
                  class="my-8 w-full h-[560px] rounded-lg bg-center bg-no-repeat bg-cover object-cover"
                  :style="{
                    backgroundImage: `url(${media?.itemImageSrc})`,
                  }"
                ></div>
              </template>
            </CustomCarousel>

            <div
              class="hidden md:block w-[440px] absolute top-16 right-16 z-10 space-y-8 py-10 px-8 bg-primary-600 rounded-xl"
            >
              <div class="">
                <p class="text-white font-bold text-xl">
                  Free consultation for this university
                </p>
                <p class="text-sm text-gray-200 text-normal mt-2">
                  We are ready to help you with your difficulties and
                  processing.
                </p>
              </div>

              <div class="grid grid-cols-2 gap-4 text-[#EFF6FF]">
                <div
                  class="flex items-start flex-nowrap p-2 rounded-xl gap-4 bg-[#496c9566]"
                >
                  <i class="pi pi-building text-xl mt-1"></i>
                  <div class="space-y-1.5">
                    <p class="text-sm font-normal">Founded year</p>
                    <p class="text-[18px] font-bold">
                      {{
                        getAttributeData("founded_date")
                          ? new Date(
                              getAttributeData("founded_date"),
                            ).getFullYear()
                          : "Not provided yet"
                      }}
                    </p>
                  </div>
                </div>
                <div
                  class="flex items-start flex-nowrap p-2 rounded-xl gap-4 bg-[#496c9566]"
                >
                  <i class="pi pi-book text-xl mt-1"></i>
                  <div class="space-y-1.5">
                    <p class="text-sm font-normal">Programs</p>
                    <p class="text-[18px] font-bold">
                      {{
                        university?.data?.program_count > 0
                          ? university.data.program_count
                          : "Not provided yet"
                      }}
                    </p>
                  </div>
                </div>
                <div
                  class="flex items-start flex-nowrap p-2 rounded-xl gap-4 bg-[#496c9566]"
                >
                  <i class="pi pi-users text-xl mt-1"></i>
                  <div class="space-y-1.5">
                    <p class="text-sm font-normal">No. of Student Mentors</p>
                    <p class="text-[18px] font-bold">
                      {{ numberFormatter.format(exploreCount?.data?.mentors) }}
                    </p>
                  </div>
                </div>
                <div
                  class="flex items-start flex-nowrap p-2 rounded-xl gap-4 bg-[#496c9566]"
                >
                  <i class="pi pi-map-marker text-xl mt-1"></i>
                  <div class="space-y-1.5">
                    <p class="text-sm font-normal">Location</p>
                    <p
                      :class="{
                        'text-[18px] font-bold':
                          university?.data?.cities?.length > 0,
                        'text-base text-white':
                          university?.data?.cities?.length === 0,
                      }"
                    >
                      {{
                        university?.data?.cities?.length > 0
                          ? getCitiesName(university?.data?.cities)
                          : "Not provided yet"
                      }}
                    </p>
                  </div>
                </div>
              </div>
              <div class="space-y-4">
                <ChooseSchedule :university="university?.data" />

                <div class="flex items-center space-x-[-12px]">
                  <div
                    v-for="(img, index) in mentorImages"
                    :key="index"
                    class="w-10 h-10 rounded-full border-2 border-white overflow-hidden"
                  >
                    <img
                      :src="img"
                      alt="Mentor"
                      class="w-full h-full object-cover"
                    />
                  </div>
                  <div
                    class="w-10 h-10 rounded-full border-2 border-white bg-white flex items-center justify-center text-sm font-medium text-blue-700"
                  >
                    +48
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ALL -->
        <div class="grid grid-cols-12 gap-16 mt-4">
          <ul class="col-span-3">
            <li
              v-for="item in sidebarItems"
              :key="item + 'program-detail-side-bar-item-key'"
              class="h-9 flex items-center gap-3 hover:bg-gray-200 cursor-pointer"
              @click="() => selectSidebar(item)"
            >
              <div
                :class="[
                  'w-1 h-full rounded-r-lg',
                  activeSidebar === item ? 'bg-primary-500' : '',
                ]"
              ></div>
              <p
                :class="[
                  'text-sm',
                  activeSidebar === item
                    ? 'font-bold text-primary-500'
                    : 'font-semibold text-[#6B7280]',
                ]"
              >
                {{ item }}
              </p>
            </li>
          </ul>

          <div class="col-span-9 space-y-20 mt-2">
            <!-- Key Information -->
            <TitleSection
              showDivider
              title="KEY INFORMATION"
              id="Key information"
            >
              <div class="grid grid-cols-12 w-full gap-4">
                <KeyInfoCard
                  rootClass="bg-[#F5F5F5] border border-[#F5F5F5] p-6 w-full"
                  :label="'Asia Ranking ' + new Date().getFullYear()"
                  icon="pi-building"
                  :value="
                    getRankingLabel(university?.data?.ranking) ||
                    'Not provided yet'
                  "
                />

                <KeyInfoCard
                  rootClass="bg-[#F5F5F5] border border-[#F5F5F5] p-6 w-full"
                  label="Programs"
                  icon="pi-book"
                  :value="
                    university?.data?.program_count > 0
                      ? numberFormatter.format(university?.data?.program_count)
                      : 'Not provided yet'
                  "
                />

                <KeyInfoCard
                  rootClass="bg-[#F5F5F5] border border-[#F5F5F5] p-6 w-full"
                  label="Number of campus"
                  icon="pi-graduation-cap"
                  :value="
                    university?.data?.cities?.length > 0
                      ? university?.data?.cities?.length === 1
                        ? 'Single Campus'
                        : numberFormatter.format(
                            university?.data?.cities?.length,
                          )
                      : 'Data Not Available'
                  "
                />

                <KeyInfoCard
                  rootClass="bg-[#F5F5F5] border border-[#F5F5F5] p-6 w-full"
                  label="Total Enrollment"
                  icon="pi-users"
                  :value="
                    getAttributeData('total_enrollment') > 0
                      ? numberFormatter.format(
                          getAttributeData('total_enrollment'),
                        )
                      : 'Data Not Available'
                  "
                />

                <KeyInfoCard
                  rootClass="bg-[#F5F5F5] border border-[#F5F5F5] p-6 w-full"
                  label="Founded"
                  icon="pi-calendar"
                  :value="
                    getAttributeData('founded_date')
                      ? format(getAttributeData('founded_date'), 'dd MMM yyyy')
                      : 'Not provided yet'
                  "
                />

                <KeyInfoCard
                  rootClass="bg-[#F5F5F5] border border-[#F5F5F5] p-6 w-full"
                  label="Location"
                  icon="pi-map-marker"
                  :value="
                    getCitiesName(university?.data?.cities) ||
                    'Not provided yet'
                  "
                />

                <KeyInfoCard
                  rootClass="bg-[#F5F5F5] border border-[#F5F5F5] p-6 w-full"
                  label="Study mode"
                  icon="pi-book"
                  :value="getAttributeData('study_mode') || 'Not provided yet'"
                />

                <KeyInfoCard
                  rootClass="bg-[#F5F5F5] border border-[#F5F5F5] p-6 w-full"
                  label="Schedule preferences"
                  icon="pi-clock"
                  :value="
                    getAttributeData('schedule_preference') ||
                    'Not provided yet'
                  "
                />
                <KeyInfoCard
                  rootClass="bg-[#F5F5F5] border border-[#F5F5F5] p-6 w-full"
                  label="Employability Rate"
                  icon="pi-clock"
                  :value="
                    getAttributeData('employability_rate') !== 'N/A' &&
                    getAttributeData('employability_rate') > 0
                      ? getAttributeData('employability_rate') + ' %'
                      : 'Data Not Available'
                  "
                />
              </div>
            </TitleSection>

            <!-- About -->
            <TitleSection title="ABOUT UNIVERSITY" id="About university">
              <div
                v-html="university?.data?.about"
                class="ql-editor editor-text text-gray-800 text-base leading-[1.8] prose prose-neutral max-w-none"
              />
            </TitleSection>

            <TitleSection title="Programs" id="Programs">
              <Tabs :value="Object.keys(programsGroupByDegree || {})[0] || ''">
                <TabList>
                  <Tab
                    v-for="degree in degrees?.filter(
                      (degree) =>
                        programsGroupByDegree?.[degree.value]?.length > 0,
                    )"
                    :value="degree.value"
                    class=""
                  >
                    <div class="flex items-start gap-2">
                      <span class="font-semibold">{{ degree.label }}</span>
                      <Badge
                        :value="programsGroupByDegree?.[degree.value]?.length"
                        size="small"
                        severity="secondary"
                      ></Badge>
                    </div>
                  </Tab>
                </TabList>
                <TabPanels>
                  <TabPanel v-for="degree in degrees" :value="degree.value">
                    <ul
                      class="space-y-2 mt-4"
                      v-if="programsGroupByDegree?.[degree.value]"
                    >
                      <li
                        v-for="program in programsGroupByDegree?.[degree.value]"
                        :key="program?.id + 'program-list-key'"
                      >
                        <NuxtLink :to="`/programs/${program?.slug}`">
                          <p
                            class="font-normal text-sm md:text-base hover:underline underline-offset-4 text-primary-500"
                          >
                            {{ program?.name }}
                          </p>
                        </NuxtLink>
                      </li>
                    </ul>
                    <p v-else class="text-sm italic text-muted mt-5">
                      No program
                    </p>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </TitleSection>

            <TitleSection title="SCHOLARSHIP" id="Scholarship">
              <div class="cms-table">
                <DataTable
                  v-if="university?.data?.scholarships?.data?.length > 0"
                  :value="university?.data?.scholarships?.data"
                  showGridlines
                  tableStyle="min-width: 50rem"
                  class="text-sm text-gray-800"
                >
                  <Column
                    field="name"
                    header="Scholarship"
                    style="width: 23%"
                  ></Column>
                  <Column
                    field="requirements"
                    header="Requirement"
                    style="width: 43%"
                  >
                    <template #body="{ data }">
                      <RichTextFormatter
                        :text="data?.requirements"
                      /> </template
                  ></Column>
                  <Column
                    field="benefits"
                    header="Benefit"
                    style="width: 33%"
                  ></Column>
                </DataTable>
                <p v-else class="text-sm italic text-muted mt-5">
                  No scholarship programs available at the moment
                </p>
              </div>
            </TitleSection>

            <!-- <TitleSection
              title="Campus overview"
              id="Campus features"
            ></TitleSection> -->

            <!-- <TitleSection
              title="ACCOMMODATION"
              id="Accommodation"
            ></TitleSection> -->

            <TitleSection
              title="HONEST REVIEW"
              id="Review"
              v-if="university?.data?.testimonials?.data?.length > 0"
            >
              <CustomCarousel
                :slidesPerView="
                  university?.data?.testimonials?.data?.length > 2
                    ? slidesPerView
                    : 1
                "
                :data="university?.data?.testimonials?.data"
              >
                <template #default="{ data: reviewer }">
                  <ReviewCard
                    icon="pi-book"
                    :profileUrl="getTestimonialImage(reviewer.media)"
                    :content="reviewer?.content"
                    :name="reviewer?.name"
                    :position="reviewer?.position"
                  />
                </template>
              </CustomCarousel>
              <!-- <div class="flex flex-row gap-6 overflow-x-auto w-full p-2">
                <ReviewCard
                  v-for="testimonial in university?.data?.testimonials?.data"
                  :key="testimonial.id"
                  :content="testimonial.content"
                  :name="testimonial.name"
                  :position="testimonial.position"
                  :profileUrl="getTestimonialImage(testimonial.media)"
                />
              </div> -->
            </TitleSection>

            <TitleSection title="LOCATION" id="Location">
              <KeyInfoCard
                valueClass="text-sm font-semibold"
                iconClass="text-xl"
                label="Location"
                icon="pi-map-marker"
              >
                <div
                  v-if="university?.data?.cities?.length < 1"
                  class="text-sm italic"
                >
                  Data not available
                </div>
                <div
                  v-else
                  v-for="city in university?.data?.cities"
                  :key="city.id"
                  class="text-sm sm:text-base font-bold text-primary-500"
                  :class="{
                    'hover:underline': city?.map_url,
                  }"
                >
                  <a v-if="city?.map_url" :href="city?.map_url" target="_blank">
                    {{ city?.address }}
                  </a>
                  <span v-else>{{ city?.address }}</span>
                </div>
              </KeyInfoCard>

              <KeyInfoCard
                valueClass="text-sm font-semibold"
                iconClass="text-xl"
                label="Phone"
                icon="pi-phone"
              >
                <a
                  href="tel:+66806759374"
                  class="text-primary-500 hover:underline font-bold"
                >
                  +66 80-675-9374
                </a>
              </KeyInfoCard>

              <KeyInfoCard
                valueClass="text-sm font-semibold"
                iconClass="text-xl"
                label="Mail"
                icon="pi-envelope"
              >
                <a
                  href="mailto:info@unilinks.co"
                  class="text-primary-500 hover:underline font-bold"
                >
                  info@unilinks.co
                </a>
              </KeyInfoCard>
            </TitleSection>

            <!-- <TitleSection title="Blog" id="News"></TitleSection> -->
          </div>
        </div>
      </div>

      <!-- mobile -->
      <div class="block md:hidden px-4 mt-6 mb-20">
        <!-- University Info -->
        <div class="flex flex-col gap-4">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-4">
              <Avatar
                v-if="getUniversityLogo(university?.data?.media)"
                :image="getUniversityLogo(university?.data?.media)"
                class="mr-2 w-14 h-14"
                size="xlarge"
              />
              <Avatar
                v-else
                :label="university?.data?.name?.slice(0, 1)"
                class="mr-2 w-14 h-14"
                size="xlarge"
              />
              <div>
                <h1 class="text-primary-500 font-bold text-2xl">
                  {{ university?.data?.name }}
                </h1>
                <div class="text-sm text-gray-600">
                  {{ university?.data?.type }} ·
                  {{ getRankingLabel(university?.data?.ranking) }}
                </div>
              </div>
            </div>
            <WishlistBtn :id="university?.data?.id" />
          </div>

          <!-- Banner Carousel -->
          <div class="mt-4">
            <CustomCarousel
              v-if="universityImages"
              :slidesPerView="1"
              :data="universityImages"
            >
              <template #default="{ data: media }">
                <div
                  class="w-full h-[200px] rounded-lg bg-center bg-no-repeat bg-cover"
                  :style="{ backgroundImage: `url(${media?.itemImageSrc})` }"
                ></div>
              </template>
            </CustomCarousel>
            <div v-else class="w-full h-[200px] rounded-lg bg-slate-200"></div>
          </div>

          <!-- Here uni consult -->
          <div class="space-y-8 py-8 px-4 bg-primary-600 rounded-xl">
            <div class="">
              <p class="text-white font-bold text-xl">
                Free consultation for this university
              </p>
              <p class="text-sm text-gray-200 text-normal mt-2">
                We are ready to help you with your difficulties and processing.
              </p>
            </div>

            <div class="grid grid-cols-1 gap-4 text-[#EFF6FF]">
              <div
                class="flex items-start flex-nowrap p-2 rounded-xl gap-4 bg-[#496c9566]"
              >
                <i class="pi pi-building text-xl mt-1"></i>
                <div class="space-y-1.5">
                  <p class="text-sm font-normal">Founded year</p>
                  <p class="text-[18px] font-bold">
                    {{
                      getAttributeData("founded_date")
                        ? new Date(
                            getAttributeData("founded_date"),
                          ).getFullYear()
                        : "Not provided yet"
                    }}
                  </p>
                </div>
              </div>
              <div
                class="flex items-start flex-nowrap p-2 rounded-xl gap-4 bg-[#496c9566]"
              >
                <i class="pi pi-book text-xl mt-1"></i>
                <div class="space-y-1.5">
                  <p class="text-sm font-normal">Programs</p>
                  <p class="text-[18px] font-bold">
                    {{
                      university?.data?.program_count > 0
                        ? university.data.program_count
                        : "Not provided yet"
                    }}
                  </p>
                </div>
              </div>
              <div
                class="flex items-start flex-nowrap p-3 rounded-xl gap-4 bg-[#496c9566]"
              >
                <i class="pi pi-users text-xl mt-1"></i>
                <div class="space-y-1.5">
                  <p class="text-sm font-normal">No. of Student Mentors</p>
                  <p class="text-[18px] font-bold">
                    {{ numberFormatter.format(exploreCount?.data?.mentors) }}
                  </p>
                </div>
              </div>
              <div
                class="flex items-start flex-nowrap p-2 rounded-xl gap-4 bg-[#496c9566]"
              >
                <i class="pi pi-map-marker text-xl mt-1"></i>
                <div class="space-y-1.5">
                  <p class="text-sm font-normal">Location</p>
                  <p
                    :class="{
                      'text-[18px] font-bold':
                        university?.data?.cities?.length > 0,
                      'text-base text-white':
                        university?.data?.cities?.length === 0,
                    }"
                  >
                    {{
                      university?.data?.cities?.length > 0
                        ? getCitiesName(university?.data?.cities)
                        : "Not provided yet"
                    }}
                  </p>
                </div>
              </div>
            </div>
            <div class="space-y-4">
              <ChooseSchedule :university="university?.data" />

              <div class="flex items-center space-x-[-12px]">
                <div
                  v-for="(img, index) in mentorImages"
                  :key="index"
                  class="w-10 h-10 rounded-full border-2 border-white overflow-hidden"
                >
                  <img
                    :src="img"
                    alt="Mentor"
                    class="w-full h-full object-cover"
                  />
                </div>
                <div
                  class="w-10 h-10 rounded-full border-2 border-white bg-white flex items-center justify-center text-sm font-medium text-blue-700"
                >
                  +48
                </div>
              </div>
            </div>
          </div>

          <!-- Sections -->
          <div class="space-y-10 mt-6">
            <!-- Key Info -->
            <TitleSection title="Key Information">
              <div class="grid grid-cols-2 gap-4">
                <KeyInfoCard
                  v-for="item in [
                    {
                      label: 'Asia Ranking ' + new Date().getFullYear(),
                      icon: 'pi-building',
                      value: getRankingLabel(university?.data?.ranking),
                    },
                    {
                      label: 'Programs',
                      icon: 'pi-book',
                      value: university?.data?.program_count,
                    },
                    {
                      label: 'Number of campus',
                      icon: 'pi-graduation-cap',
                      value:
                        university?.data?.cities?.length > 0
                          ? university?.data?.cities?.length === 1
                            ? 'Single campus'
                            : numberFormatter.format(
                                university?.data?.cities?.length,
                              )
                          : 'Data Not Available',
                    },
                    {
                      label: 'Total Enrollment',
                      icon: 'pi-users',
                      value:
                        getAttributeData('total_enrollment') > 0
                          ? numberFormatter.format(
                              getAttributeData('total_enrollment'),
                            )
                          : 'Data Not Available',
                    },
                    {
                      label: 'Founded',
                      icon: 'pi-calendar',
                      value: format(
                        getAttributeData('founded_date'),
                        'dd MMM yyyy',
                      ),
                    },
                    {
                      label: 'Location',
                      icon: 'pi-map-marker',
                      value: getCitiesName(university?.data?.cities),
                    },
                    {
                      label: 'Study mode',
                      icon: 'pi-book',
                      value: getAttributeData('study_mode'),
                    },
                    {
                      label: 'Schedule preferences',
                      icon: 'pi-clock',
                      value: getAttributeData('schedule_preference'),
                    },
                    {
                      label: 'Employability Rate',
                      icon: 'pi-clock',
                      value:
                        getAttributeData('employability_rate') !== 'N/A' &&
                        getAttributeData('employability_rate') > 0
                          ? getAttributeData('employability_rate') + ' %'
                          : 'Data Not Available',
                    },
                  ]"
                  :key="item.label"
                  :label="item.label"
                  :icon="item.icon"
                  :value="item.value"
                  rootClass="bg-[#F5F5F5] border border-[#F5F5F5] p-4"
                />
              </div>
            </TitleSection>

            <!-- About -->
            <TitleSection title="About University">
              <div
                v-html="university?.data?.about"
                class="ql-editor editor-text text-sm leading-6 max-w-full"
              ></div>
            </TitleSection>

            <!-- Programs -->
            <TitleSection title="Programs" id="Programs">
              <Tabs :value="Object.keys(programsGroupByDegree || {})[0] || ''">
                <TabList>
                  <Tab
                    v-for="degree in degrees?.filter(
                      (degree) =>
                        programsGroupByDegree?.[degree.value]?.length > 0,
                    )"
                    :value="degree.value"
                  >
                    <div class="flex items-start gap-2">
                      <span class="font-semibold">{{ degree.label }}</span>
                      <Badge
                        :value="programsGroupByDegree?.[degree.value]?.length"
                        size="small"
                      ></Badge>
                    </div>
                  </Tab>
                </TabList>
                <TabPanels>
                  <TabPanel v-for="degree in degrees" :value="degree.value">
                    <ul
                      class="space-y-2 mt-4"
                      v-if="programsGroupByDegree?.[degree.value]"
                    >
                      <li
                        v-for="program in programsGroupByDegree?.[degree.value]"
                        :key="program?.id + 'program-list-key'"
                      >
                        <NuxtLink :to="`/programs/${program?.slug}`">
                          <p
                            class="font-normal text-sm md:text-base hover:underline underline-offset-4 text-primary-500"
                          >
                            {{ program?.name }}
                          </p>
                        </NuxtLink>
                      </li>
                    </ul>
                    <p v-else class="text-sm italic text-muted mt-5">
                      No program
                    </p>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </TitleSection>

            <!-- Scholarships -->
            <TitleSection title="Scholarship">
              <div
                class="space-y-4"
                v-if="university?.data?.scholarships?.data?.length > 0"
              >
                <div
                  v-for="sch in university?.data?.scholarships?.data"
                  :key="sch.id"
                  class="border p-3 md:p-4 rounded-md text-sm space-y-2"
                >
                  <p class="font-bold">{{ sch.name }}</p>
                  <div>
                    <span class="font-semibold">Requirement:</span>
                    <div
                      v-html="sch.requirements"
                      class="ql-editor editor-text"
                    ></div>
                  </div>
                  <p class="pt-2.5">
                    <span class="font-semibold">Benefit:</span>
                    {{ sch.benefits }}
                  </p>
                </div>
              </div>
              <p v-else class="text-sm italic text-muted mt-5">
                No scholarship programs available at the moment
              </p>
            </TitleSection>

            <!-- Reviews -->
            <TitleSection
              title="Honest Review"
              v-if="university?.data?.testimonials?.data?.length > 0"
            >
              <CustomCarousel
                :slidesPerView="
                  university?.data?.testimonials?.data?.length > 2
                    ? slidesPerView
                    : 1
                "
                :data="university?.data?.testimonials?.data"
              >
                <template #default="{ data: reviewer }">
                  <ReviewCard
                    icon="pi-book"
                    :profileUrl="getTestimonialImage(reviewer.media)"
                    :content="reviewer?.content"
                    :name="reviewer?.name"
                    :position="reviewer?.position"
                  />
                </template>
              </CustomCarousel>
            </TitleSection>

            <!-- Location -->
            <TitleSection title="LOCATION" id="Location">
              <KeyInfoCard
                valueClass="text-sm font-semibold"
                iconClass="text-xl"
                label="Location"
                icon="pi-map-marker"
              >
                <div
                  v-for="city in university?.data?.cities"
                  :key="city.id"
                  class="text-sm sm:text-base font-bold text-primary-500"
                  :class="{
                    'hover:underline': city?.map_url,
                  }"
                >
                  <a v-if="city?.map_url" :href="city?.map_url" target="_blank">
                    {{ city?.address }}
                  </a>
                  <span v-else>{{ city?.address }}</span>
                </div>
              </KeyInfoCard>

              <KeyInfoCard
                valueClass="text-sm font-semibold"
                iconClass="text-xl"
                label="Phone"
                icon="pi-phone"
                :value="university?.data?.university_address?.contact_number"
              />

              <KeyInfoCard
                valueClass="text-sm font-semibold"
                iconClass="text-xl"
                label="Mail"
                icon="pi-envelope"
                :value="university?.data?.university_address?.email"
              />
            </TitleSection>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  </NuxtLayout>
</template>
